import React, { useEffect, useState, useMemo, useCallback } from "react";
import { fetchAutomaticResponseHistory } from "./DataServices/AutomaticResponsesDataServices";
import { FetchAppfolioData } from "./DataServices/UnprocessedEmailService";
import {
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import ReactDOM from "react-dom";
import { sendAppfolioMatchEmail } from "./DataServices/ReminderService";
import Loading from "./Loading";

interface IAutomaticResponseHistory {
  id: string;
  idAutomaticResponse: string;
  moveInDate: Date;
  rent: number;
  sentDate: Date;
  idProspectiveTenant: string;
  suggestedAppointmentID: string;
  confirmed: boolean;
  rejected: boolean;
  cancelled: boolean;
  firstName: string;
  lastName: string;
  listingID: string;
  email: string;
  title: string;
  startTime: Date;
  endTime: Date;
  notes: string;
  phone: string;
  resend: number | null;
}

interface MemberMatcherProps {
  memberGuarantorInfos?: { [key: string]: any };
  mappingInfo?: { [key: string]: any };
}

interface MemberGuarantorInfo {
  id: string;
  name: string;
  email: string;
  isGuarantor: boolean;
  confidence: number;
}

interface IAppfolioData {
  id: string;
  propertyName: string;
  applicationTime: string;
  tenantName: string;
  gaurantorName: string;
}

interface ApartmentDetail {
  title: string;
  count: number;
  applications: IAutomaticResponseHistory[];
}

interface ButtonProperties {
  text: string;
  bgColor: string;
  ringColor: string;
  disabled: boolean;
}

interface Notification {
  id: string;
  message: string;
  type: "success" | "error";
  show: boolean;
}

const MemberMatcher: React.FC<MemberMatcherProps> = ({
  memberGuarantorInfos = {},
  mappingInfo = {},
}) => {
  const [automaticResponseHistory, setAutomaticResponseHistory] = useState<
    IAutomaticResponseHistory[]
  >([]);
  const [apfolioMembers, setApfolioMembers] = useState<IAppfolioData[]>([]);
  const [clickedMember, setClickedMember] = useState<string | null>(null);
  const [apartmentFilterOptions, setApartmentFilterOptions] = useState<
    string[]
  >([]);
  const [showApartmentDropdown, setShowApartmentDropdown] = useState(false);
  const [apartmentDropdownPosition, setApartmentDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [filters, setFilters] = useState({
    member: "",
    email: "",
  });
  const [sortByEmailEnabled, setSortByEmailEnabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [externalFormStatusFilter, setExternalFormStatusFilter] = useState<
    string[]
  >([]);
  const [showExternalFormStatusDropdown, setShowExternalFormStatusDropdown] =
    useState(false);
  const [externalFormDropdownPosition, setExternalFormDropdownPosition] =
    useState({ top: 0, left: 0, width: 0 });
  const [showMobileFilters, setShowMobileFilters] = useState(true);

  const showNotification = useCallback(
    (message: string, type: "success" | "error") => {
      const id = Date.now().toString();
      setNotification({ id, message, type, show: true });
      setTimeout(() => {
        setNotification((prev) => (prev ? { ...prev, show: false } : null));
        setTimeout(() => setNotification(null), 300);
      }, 5000);
    },
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [arhPaged, appfolioData] = await Promise.all([
          fetchAutomaticResponseHistory("All", false, 0, 1000),
          FetchAppfolioData(),
        ]);

        setAutomaticResponseHistory(arhPaged.items ?? []);
        setApfolioMembers(appfolioData);
      } catch (error) {
        console.error("Error fetching data:", error);
        showNotification("Failed to load data", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [showNotification]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".relative") && !target.closest(".fixed")) {
        setShowApartmentDropdown(false);
        setClickedMember(null);
        setShowExternalFormStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const uniqueApartmentNames = useMemo(() => {
    const titleSet = new Set<string>();
    automaticResponseHistory.forEach((m) => titleSet.add(m.title));
    return Array.from(titleSet).sort();
  }, [automaticResponseHistory]);

  const latestUniqueMembers = useMemo(() => {
    const memberMap = new Map<string, IAutomaticResponseHistory>();

    automaticResponseHistory.forEach((item) => {
      const existing = memberMap.get(item.idProspectiveTenant);
      if (!existing || new Date(item.sentDate) > new Date(existing.sentDate)) {
        memberMap.set(item.idProspectiveTenant, item);
      }
    });

    return Array.from(memberMap.values());
  }, [automaticResponseHistory]);

  const apartmentCounts = useMemo(() => {
    const counts = new Map<string, number>();
    automaticResponseHistory.forEach((member) => {
      const current = counts.get(member.idProspectiveTenant) || 0;
      counts.set(member.idProspectiveTenant, current + 1);
    });
    return counts;
  }, [automaticResponseHistory]);

  const apartmentDetailsMap = useMemo(() => {
    const detailsMap = new Map<string, ApartmentDetail[]>();

    latestUniqueMembers.forEach((member) => {
      const applications = automaticResponseHistory
        .filter(
          (entry) => entry.idProspectiveTenant === member.idProspectiveTenant,
        )
        .sort(
          (a, b) =>
            new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime(),
        );

      const groupedByTitle = applications.reduce(
        (acc, app) => {
          if (!acc[app.title]) acc[app.title] = [];
          acc[app.title].push(app);
          return acc;
        },
        {} as Record<string, IAutomaticResponseHistory[]>,
      );

      const apartmentDetails: ApartmentDetail[] = Object.entries(
        groupedByTitle,
      ).map(([title, apps]) => ({
        title,
        count: apps.length,
        applications: apps.sort(
          (a, b) =>
            new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime(),
        ),
      }));

      detailsMap.set(member.idProspectiveTenant, apartmentDetails);
    });

    return detailsMap;
  }, [automaticResponseHistory, latestUniqueMembers]);

  const getExternalFormStatus = useCallback(
    (member: IAutomaticResponseHistory) => {
      // member objects may use different id fields depending on source
      const key =
        member.id || member.idProspectiveTenant || member.idAutomaticResponse;
      const info =
        memberGuarantorInfos[key] ||
        memberGuarantorInfos[member.id] ||
        memberGuarantorInfos[member.idProspectiveTenant] ||
        {};
      const mapping =
        mappingInfo[key] ||
        mappingInfo[member.id] ||
        mappingInfo[member.idProspectiveTenant] ||
        {};

      const providedMembers = info?.memberAndGaurantorInfo || [];
      const actual = providedMembers.length || 0;

      // Prefer mapping.mappingTotalMembers when available (it considers edits and provided count fallback)
      const expectedMembers =
        mapping?.mappingTotalMembers ?? mapping?.totalMembers ?? 0;

      const status =
        actual === 0
          ? "Not Started"
          : expectedMembers === 0
            ? "Partial"
            : actual >= expectedMembers
              ? "Completed"
              : "Partial";
      const color =
        status === "Not Started"
          ? "bg-gray-100 text-gray-800"
          : status === "Completed"
            ? "bg-green-100 text-green-800"
            : "bg-yellow-100 text-yellow-800";

      return { status, color };
    },
    [memberGuarantorInfos, mappingInfo],
  );

  const buttonPropsMap = useMemo(() => {
    const propsMap = new Map<string, ButtonProperties>();

    latestUniqueMembers.forEach((member) => {
      const latestApplication = automaticResponseHistory
        .filter(
          (entry) => entry.idProspectiveTenant === member.idProspectiveTenant,
        )
        .reduce((latest, current) =>
          new Date(current.sentDate) > new Date(latest.sentDate)
            ? current
            : latest,
        );

      const resendValue = latestApplication?.resend;

      let props: ButtonProperties;
      if (resendValue === 1) {
        props = {
          text: "Scheduled",
          bgColor: "bg-yellow-600",
          ringColor: "focus:ring-yellow-500",
          disabled: true,
        };
      } else if (resendValue === 2) {
        props = {
          text: "Sent",
          bgColor: "bg-green-600",
          ringColor: "focus:ring-green-500",
          disabled: true,
        };
      } else {
        props = {
          text: "Send Email",
          bgColor: "bg-blue-600 hover:bg-blue-700",
          ringColor: "focus:ring-blue-500",
          disabled: false,
        };
      }

      propsMap.set(member.idProspectiveTenant, props);
    });

    return propsMap;
  }, [latestUniqueMembers, automaticResponseHistory]);

  const getEditDistance = useCallback((str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1)
      .fill(null)
      .map(() => Array(str1.length + 1).fill(0));

    for (let i = 0; i <= str2.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= str1.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }, []);

  const getStringSimilarity = useCallback(
    (str1: string, str2: string): number => {
      if (str1 === str2) return 1.0;
      if (!str1 || !str2) return 0;

      const s1 = str1.toLowerCase();
      const s2 = str2.toLowerCase();

      if (s1 === s2) return 1.0;

      const lengthDiff = Math.abs(s1.length - s2.length);
      const maxLength = Math.max(s1.length, s2.length);

      if (lengthDiff / maxLength > 0.7) return 0; // Too different in length

      if (s1.includes(s2) || s2.includes(s1)) {
        const longer = s1.length > s2.length ? s1 : s2;
        const shorter = s1.length > s2.length ? s2 : s1;
        return shorter.length / longer.length;
      }

      const editDistance = getEditDistance(s1, s2);
      return maxLength === 0 ? 1.0 : (maxLength - editDistance) / maxLength;
    },
    [getEditDistance],
  );

  const normalizeName = useCallback((name: string): string => {
    return (
      name
        ?.toLowerCase()
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim() || ""
    );
  }, []);

  const isNameMatch = useCallback(
    (
      memberFirstName: string,
      memberLastName: string,
      compareName: string,
    ): { isMatch: boolean; confidence: number } => {
      if (!compareName || (!memberFirstName && !memberLastName)) {
        return { isMatch: false, confidence: 0 };
      }

      const memberFirst = normalizeName(memberFirstName);
      const memberLast = normalizeName(memberLastName);
      const compareWords = normalizeName(compareName)
        .split(" ")
        .filter((w) => w.length > 1);

      const memberWords = [memberFirst, memberLast].filter((w) => w.length > 1);

      let wordMatches = 0;
      for (const memberWord of memberWords) {
        const bestMatch = Math.max(
          ...compareWords.map((word) => getStringSimilarity(memberWord, word)),
        );
        if (bestMatch > 0.7) wordMatches++;
      }

      const confidence =
        memberWords.length > 0 ? wordMatches / memberWords.length : 0;
      const isMatch =
        confidence > 0.7 ||
        (wordMatches >= Math.min(2, memberWords.length) && confidence > 0.5);

      return { isMatch, confidence: Math.min(confidence, 1.0) };
    },
    [normalizeName, getStringSimilarity],
  );

  const findSimilarMatches = useCallback(
    (member: IAutomaticResponseHistory): MemberGuarantorInfo[] => {
      const firstName = member.firstName || "";
      const lastName = member.lastName || "";

      if (!firstName && !lastName) return [];

      const matches: MemberGuarantorInfo[] = [];

      const normalizeApt = (name: string) =>
        name
          ?.toLowerCase()
          .replace(/apt|#|unit/g, "")
          .replace(/\s+/g, "")
          .trim() || "";
      const groupApt = normalizeApt(member.title);

      apfolioMembers.forEach((apfolioMember) => {
        const isAptMatch =
          normalizeApt(apfolioMember.propertyName) === groupApt;

        // Check tenant name
        if (apfolioMember.tenantName) {
          const tenantMatch = isNameMatch(
            firstName,
            lastName,
            apfolioMember.tenantName,
          );

          const confidence = isAptMatch
            ? tenantMatch.confidence + 0.2
            : tenantMatch.confidence;

          if (
            (tenantMatch.isMatch && confidence > 0.7) ||
            (isAptMatch && tenantMatch.confidence > 0.5)
          ) {
            matches.push({
              id: apfolioMember.id,
              name: apfolioMember.tenantName,
              email: "",
              isGuarantor: false,
              confidence: confidence,
            });
          }
        }

        // Check guarantor name
        if (apfolioMember.gaurantorName) {
          const guarantorMatch = isNameMatch(
            firstName,
            lastName,
            apfolioMember.gaurantorName,
          );

          const confidence = isAptMatch
            ? guarantorMatch.confidence + 0.2
            : guarantorMatch.confidence;

          if (
            (guarantorMatch.isMatch && confidence > 0.7) ||
            (isAptMatch && guarantorMatch.confidence > 0.5)
          ) {
            matches.push({
              id: `${apfolioMember.id}-guarantor`,
              name: apfolioMember.gaurantorName,
              email: "",
              isGuarantor: true,
              confidence: confidence,
            });
          }
        }
      });

      return matches.sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
    },
    [apfolioMembers, isNameMatch],
  );

  const matchResults = useMemo(() => {
    return latestUniqueMembers.map((member) => ({
      member,
      matches: findSimilarMatches(member),
    }));
  }, [latestUniqueMembers, findSimilarMatches]);

  const filteredAndSortedResults = useMemo(() => {
    let results = [...matchResults];

    if (
      filters.member ||
      filters.email ||
      apartmentFilterOptions.length > 0 ||
      externalFormStatusFilter.length > 0
    ) {
      results = results.filter((result) => {
        const fullName =
          `${result.member.firstName} ${result.member.lastName}`.toLowerCase();

        const memberMatch =
          !filters.member || fullName.includes(filters.member.toLowerCase());
        const emailMatch =
          !filters.email ||
          result.member.email
            .toLowerCase()
            .includes(filters.email.toLowerCase());

        const apartmentMatch =
          apartmentFilterOptions.length === 0 ||
          (() => {
            const latestApp = automaticResponseHistory
              .filter(
                (entry) =>
                  entry.idProspectiveTenant ===
                  result.member.idProspectiveTenant,
              )
              .reduce((latest, current) =>
                new Date(current.sentDate) > new Date(latest.sentDate)
                  ? current
                  : latest,
              );
            return apartmentFilterOptions.includes(latestApp.title);
          })();

        const externalFormMatch =
          externalFormStatusFilter.length === 0 ||
          (() => {
            const externalStatus = getExternalFormStatus(result.member);
            return externalFormStatusFilter.includes(externalStatus.status);
          })();

        return memberMatch && emailMatch && apartmentMatch && externalFormMatch;
      });
    }

    // Apply sorting
    if (sortByEmailEnabled) {
      results = [...results].sort((a, b) => {
        const aHasMatches = a.matches.length > 0 ? 1 : 0;
        const bHasMatches = b.matches.length > 0 ? 1 : 0;
        return bHasMatches - aHasMatches;
      });
    }

    return results;
  }, [
    matchResults,
    filters,
    apartmentFilterOptions,
    externalFormStatusFilter,
    sortByEmailEnabled,
    automaticResponseHistory,
    getExternalFormStatus,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters,
    apartmentFilterOptions,
    externalFormStatusFilter,
    sortByEmailEnabled,
  ]);

  const getPaginatedResults = useCallback(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAndSortedResults.slice(startIndex, endIndex);
  }, [filteredAndSortedResults, currentPage, rowsPerPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedResults.length / rowsPerPage);
  }, [filteredAndSortedResults.length, rowsPerPage]);

  const handleApartmentDetailsClick = useCallback(
    (memberId: string, event: React.MouseEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setClickedMember((prev) => (prev === memberId ? null : memberId));
    },
    [],
  );

  const handleSendEmail = useCallback(
    async (member: IAutomaticResponseHistory, match: MemberGuarantorInfo) => {
      const latestApplication = automaticResponseHistory
        .filter(
          (entry) => entry.idProspectiveTenant === member.idProspectiveTenant,
        )
        .reduce((latest, current) =>
          new Date(current.sentDate) > new Date(latest.sentDate)
            ? current
            : latest,
        );

      if (!latestApplication?.id) {
        showNotification("No application ID found", "error");

        return;
      }

      const success = await sendAppfolioMatchEmail(latestApplication.id);

      if (success) {
        setAutomaticResponseHistory((prevHistory) =>
          prevHistory.map((entry) =>
            entry.id === latestApplication.id ? { ...entry, resend: 1 } : entry,
          ),
        );
        showNotification(
          `Email scheduled for ${
            member?.firstName === null ? "" : member?.firstName
          } ${member?.lastName === null ? "" : member?.lastName}`,
          "success",
        );
      } else {
        showNotification("Email scheduling failed", "error");
      }
    },
    [automaticResponseHistory, showNotification],
  );

  const defaultButtonProps: ButtonProperties = {
    text: "Send Email",
    bgColor: "bg-blue-600 hover:bg-blue-700",
    ringColor: "focus:ring-blue-500",
    disabled: false,
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-[10000] transform transition-all duration-300 ease-in-out ${
            notification.show
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          }`}
        >
          <div
            className={`px-4 py-3 rounded-lg shadow-lg border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-400 text-green-800"
                : "bg-red-50 border-red-400 text-red-800"
            }`}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {notification.type === "success" ? (
                  <svg
                    className="h-5 w-5 text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-red-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{notification.message}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() =>
                    setNotification((prev) =>
                      prev ? { ...prev, show: false } : null,
                    )
                  }
                  className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    notification.type === "success"
                      ? "text-green-500 hover:bg-green-100 focus:ring-green-600"
                      : "text-red-500 hover:bg-red-100 focus:ring-red-600"
                  }`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full h-full">
        <div className="bg-white shadow-sm border border-gray-200 overflow-hidden h-full">
          <div className="flex flex-col md:flex-row h-full">
            {/* Filters Sidebar with Toggle */}
            <div className="relative flex">
              {/* Toggle Arrow Button */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-600 text-white p-2 rounded-r-md shadow-lg hover:bg-gray-700 transition-all"
                style={{ left: showMobileFilters ? "256px" : "0" }}
              >
                {showMobileFilters ? (
                  <KeyboardArrowLeft className="w-5 h-5" />
                ) : (
                  <KeyboardArrowRight className="w-5 h-5" />
                )}
              </button>

              {/* Sidebar Content */}
              <div
                className={`bg-gray-50 border-r border-gray-200 overflow-y-auto transition-all duration-300 ${
                  showMobileFilters ? "w-64" : "w-0"
                }`}
              >
                <div
                  className={`p-4 md:p-6 ${showMobileFilters ? "" : "hidden"}`}
                >
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Filters
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tenant Name
                      </label>
                      <input
                        type="text"
                        placeholder="Filter by Tenant name..."
                        value={filters.member}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            member: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="text"
                        placeholder="Filter by email..."
                        value={filters.email}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Apartment
                      </label>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setApartmentDropdownPosition({
                              top: rect.bottom + window.scrollY,
                              left: rect.left + window.scrollX,
                              width: rect.width,
                            });
                            setShowApartmentDropdown(!showApartmentDropdown);
                          }}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm text-left bg-white flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <span className="text-gray-700">
                            {apartmentFilterOptions.length > 0
                              ? `${apartmentFilterOptions.length} selected`
                              : "All"}
                          </span>
                          <KeyboardArrowDown className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>

                      {showApartmentDropdown &&
                        ReactDOM.createPortal(
                          <div
                            className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                            style={{
                              top: apartmentDropdownPosition.top,
                              left: apartmentDropdownPosition.left,
                              width: apartmentDropdownPosition.width,
                            }}
                          >
                            {uniqueApartmentNames.map((apartmentName) => (
                              <div
                                key={apartmentName}
                                className="flex items-center px-3 py-2 hover:bg-gray-50"
                              >
                                <input
                                  type="checkbox"
                                  id={`apt-${apartmentName}`}
                                  checked={apartmentFilterOptions.includes(
                                    apartmentName,
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setApartmentFilterOptions([
                                        ...apartmentFilterOptions,
                                        apartmentName,
                                      ]);
                                    } else {
                                      setApartmentFilterOptions(
                                        apartmentFilterOptions.filter(
                                          (name) => name !== apartmentName,
                                        ),
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded mr-2"
                                />
                                <label
                                  htmlFor={`apt-${apartmentName}`}
                                  className="text-sm text-gray-700 cursor-pointer flex-1"
                                >
                                  {apartmentName}
                                </label>
                              </div>
                            ))}
                          </div>,
                          document.body,
                        )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        External Form Status
                      </label>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setExternalFormDropdownPosition({
                              top: rect.bottom + window.scrollY,
                              left: rect.left + window.scrollX,
                              width: rect.width,
                            });
                            setShowExternalFormStatusDropdown(
                              !showExternalFormStatusDropdown,
                            );
                          }}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm text-left bg-white flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <span className="text-gray-700">
                            {externalFormStatusFilter.length > 0
                              ? `${externalFormStatusFilter.length} selected`
                              : "All"}
                          </span>
                          <KeyboardArrowDown className="w-4 h-4 text-gray-400" />
                        </button>

                        {showExternalFormStatusDropdown &&
                          ReactDOM.createPortal(
                            <div
                              className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                              style={{
                                top: externalFormDropdownPosition.top,
                                left: externalFormDropdownPosition.left,
                                width: externalFormDropdownPosition.width,
                              }}
                            >
                              {["Not Started", "Partial", "Completed"].map(
                                (status) => (
                                  <div
                                    key={status}
                                    className="flex items-center px-3 py-2 hover:bg-gray-50"
                                  >
                                    <input
                                      type="checkbox"
                                      id={`ext-${status}`}
                                      checked={externalFormStatusFilter.includes(
                                        status,
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setExternalFormStatusFilter([
                                            ...externalFormStatusFilter,
                                            status,
                                          ]);
                                        } else {
                                          setExternalFormStatusFilter(
                                            externalFormStatusFilter.filter(
                                              (s) => s !== status,
                                            ),
                                          );
                                        }
                                      }}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded mr-2"
                                    />
                                    <label
                                      htmlFor={`ext-${status}`}
                                      className="text-sm text-gray-700 cursor-pointer flex-1"
                                    >
                                      {status}
                                    </label>
                                  </div>
                                ),
                              )}
                            </div>,
                            document.body,
                          )}
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={sortByEmailEnabled}
                          onChange={(e) =>
                            setSortByEmailEnabled(e.target.checked)
                          }
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          Sort by email enabled
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="flex-1 overflow-x-auto overflow-y-auto max-h-[calc(100vh-180px)] min-w-0">
                <div className="overflow-x-auto overflow-y-auto min-w-full">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tenant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          External Form Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Similar Matches
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-12 text-center">
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        getPaginatedResults().map((result, index) => (
                          <tr
                            key={result.member.id}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <svg
                                      className="w-5 h-5 text-blue-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {result.member.firstName}{" "}
                                    {result.member.lastName}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 relative">
                              <div className="text-sm text-gray-900 max-w-xs">
                                <div className="font-medium">
                                  {result.member.title}
                                </div>

                                {(apartmentCounts.get(
                                  result.member.idProspectiveTenant,
                                ) || 0) > 1 && (
                                  <div className="mt-1 relative">
                                    <button
                                      onClick={(e) =>
                                        handleApartmentDetailsClick(
                                          result.member.id,
                                          e,
                                        )
                                      }
                                      className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer flex items-center"
                                    >
                                      Applied for{" "}
                                      {apartmentCounts.get(
                                        result.member.idProspectiveTenant,
                                      ) || 0}
                                      <svg
                                        className={`w-3 h-3 ml-1 transition-transform duration-200 ${
                                          clickedMember === result.member.id
                                            ? "rotate-180"
                                            : ""
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    </button>

                                    {clickedMember === result.member.id &&
                                      ReactDOM.createPortal(
                                        <div
                                          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg w-80 max-h-96 overflow-y-auto p-4"
                                          style={{
                                            top: dropdownPosition.top,
                                            left: dropdownPosition.left,
                                          }}
                                        >
                                          <div className="space-y-2">
                                            {(
                                              apartmentDetailsMap.get(
                                                result.member
                                                  .idProspectiveTenant,
                                              ) || []
                                            ).map(
                                              (
                                                apartment: ApartmentDetail,
                                                idx: number,
                                              ) => (
                                                <div
                                                  key={idx}
                                                  className="border-b border-gray-200 last:border-b-0 pb-2 last:pb-0"
                                                >
                                                  <div className="flex items-center justify-between mb-1">
                                                    <span
                                                      className="font-medium text-xs text-gray-900 truncate max-w-[200px]"
                                                      title={apartment.title}
                                                    >
                                                      {apartment.title}
                                                    </span>
                                                    <span className="text-xs text-blue-600 font-medium">
                                                      {apartment.count} time
                                                      {apartment.count !== 1
                                                        ? "s"
                                                        : ""}
                                                    </span>
                                                  </div>
                                                  <div className="space-y-1">
                                                    {apartment.applications.map(
                                                      (
                                                        app: IAutomaticResponseHistory,
                                                        appIdx: number,
                                                      ) => (
                                                        <div
                                                          key={appIdx}
                                                          className="text-xs text-gray-600 flex items-center justify-between"
                                                        >
                                                          <span>
                                                            {new Date(
                                                              app.sentDate,
                                                            ).toLocaleDateString(
                                                              "en-US",
                                                              {
                                                                month: "short",
                                                                day: "numeric",
                                                                year: "numeric",
                                                              },
                                                            )}
                                                          </span>
                                                          <span className="text-xs text-gray-500">
                                                            {new Date(
                                                              app.sentDate,
                                                            ).toLocaleTimeString(
                                                              "en-US",
                                                              {
                                                                hour: "2-digit",
                                                                minute:
                                                                  "2-digit",
                                                              },
                                                            )}
                                                          </span>
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        </div>,
                                        document.body,
                                      )}
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {result.member.email}
                              </div>
                              <div className="text-sm text-gray-500">
                                {result.member.phone}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                const externalStatus = getExternalFormStatus(
                                  result.member,
                                );
                                return (
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${externalStatus.color}`}
                                  >
                                    {externalStatus.status}
                                  </span>
                                );
                              })()}
                            </td>

                            <td className="px-6 py-4">
                              {result.matches.length > 0 ? (
                                <div className="space-y-3">
                                  {result.matches.map((match) => (
                                    <div
                                      key={match.id}
                                      className="flex items-center justify-between bg-blue-50 rounded-lg p-3 border border-blue-200"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                          <div
                                            className={`w-2 h-2 rounded-full ${
                                              match.isGuarantor
                                                ? "bg-orange-400"
                                                : "bg-green-400"
                                            }`}
                                          ></div>
                                        </div>
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {match.name}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {match.isGuarantor
                                              ? "Guarantor"
                                              : "Tenant"}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2 text-gray-400">
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                  </svg>
                                  <span className="text-sm">No matches</span>
                                </div>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {result.matches.length > 0 ? (
                                (() => {
                                  const firstMatch = result.matches[0];
                                  const buttonProps =
                                    buttonPropsMap.get(
                                      result.member.idProspectiveTenant,
                                    ) || defaultButtonProps;
                                  return (
                                    <button
                                      key={firstMatch.id}
                                      onClick={() =>
                                        !buttonProps.disabled &&
                                        handleSendEmail(
                                          result.member,
                                          firstMatch,
                                        )
                                      }
                                      className={`px-3 py-1 text-xs font-medium text-white rounded-md transition-colors duration-200 ${
                                        buttonProps.bgColor
                                      } focus:outline-none focus:ring-2 ${
                                        buttonProps.ringColor
                                      } focus:ring-offset-2 ${
                                        buttonProps.disabled
                                          ? "cursor-not-allowed opacity-75"
                                          : "cursor-pointer"
                                      }`}
                                    >
                                      <span className="mr-1 flex items-center justify-center gap-1">
                                        <svg
                                          className="w-3 h-3"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2 2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                          />
                                        </svg>
                                        {buttonProps.text}
                                      </span>
                                    </button>
                                  );
                                })()
                              ) : (
                                <span className="text-gray-400 text-sm">
                                  No action
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {!isLoading && getPaginatedResults().length === 0 && (
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No members found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your filters or add some member data.
                      </p>
                    </div>
                  )}

                  {!isLoading && (
                    <div className="bg-white px-6 py-4 border-t border-gray-200 flex items-center justify-between sticky bottom-0">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 pr-">
                          <span className="text-sm text-gray-700">
                            Rows per page:
                          </span>
                          <select
                            value={rowsPerPage}
                            onChange={(e) => {
                              setRowsPerPage(Number(e.target.value));
                              setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded px-2 pr-8 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                          </select>
                        </div>
                        <div className="text-sm text-gray-700">
                          Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                          {Math.min(
                            currentPage * rowsPerPage,
                            filteredAndSortedResults.length,
                          )}{" "}
                          of {filteredAndSortedResults.length} results
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className={`p-2 rounded-md ${
                            currentPage === 1
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>

                        <span className="text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>

                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className={`p-2 rounded-md ${
                            currentPage === totalPages
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberMatcher;
